
import { Component, Vue, Watch, Prop, Emit } from "vue-property-decorator";
import { State, Getter, Action, Mutation, namespace } from "vuex-class";
import { axiosInstance } from "@/axios.instance";
import { FeaturesList } from "@/store/types";
import {
	getEnhancedMapping,
	IEnhancedMapping,
	IMap,
	IMarker,
	ILayer,
	ICircle,
	ILatLng,
	ILatLngBounds,
	convertStringToLatLng,
	addMeterToCoord,
	stringToBounds,
	union,
	extend
} from "@sureview/v2-mapping";
import { get } from "lodash";
import { darkMapStyle, satelliteMapStyle, getMapStyle } from "@/scripts/mapping/mapStyle";
import { config } from "@vue/test-utils";
import { GPSService, GeoCodeResponse } from "@/services/gps.service";
import { months } from "moment";
import { ExternalMapLayer } from "@/store/external-map-layers/types";

import { MapItemTypeData, MapItemData, MapLayerData } from "@/store/map-layers/types";
import { GuardTour, GuardTourStep } from "@/store/guard-tours/types";

class LocationBounds {
	public location: ILatLng;
	public viewport: {
		northeast: ILatLng;
		southwest: ILatLng;
	};
}

class MapBounds {
	public north: number = 0;
	public south: number = 0;
	public east: number = 0;
	public west: number = 0;
	public elevation: number = 1;
}

class LayerMap {
	[s: number]: ILayer;
}
class ItemMap {
	[s: number]: IMarker;
}
class ItemObjectMap {
	[s: number]: IMarker[];
}
class ItemTypeObjectMap {
	[s: number]: ItemObjectMap;
}

class LayerTypeMap {
	[s: number]: boolean;
}

const MapLayers = namespace("mapLayers");
const ExternalMapLayerStore = namespace("externalMapLayers");
const GuardTours = namespace("GuardTours");

@Component({
	components: {}
})
export default class GuardTourSetupMap extends Vue {

	public $refs: any = {
		mappingContainer: HTMLElement
	};	

	@Prop({default: false, type: Boolean}) focusOnTour: boolean = false;

	@Prop({default: false, type: Boolean}) tourUpdatedOrCancelled: boolean = false;	

	@MapLayers.Getter("getMapLayersItemTypes") public mapItemTypes: MapItemTypeData[];
	@MapLayers.Getter("getMapLayers") public mapLayers;
	@MapLayers.Getter("getMapItemIcons") public mapIcons!: any[];
	@MapLayers.Action public loadMapLayers: any;
	@MapLayers.Action public loadMapLayersItemTypes: any;	
	@MapLayers.Mutation public setMapLayersItemType: any;

	@GuardTours.State creatingWaypoint: boolean;
	@GuardTours.State currentGuardTour: GuardTour;
	@GuardTours.State mapItemList: MapItemData[];
	@GuardTours.Mutation setWaypointMarkerPosition: (position: string) => void;
	@GuardTours.Mutation setMapItemList: (mapItemList: MapItemData[]) => void;

	@Getter("getMapKey") private mapKey: string;
	@Getter("getFeaturesList") featuresList: FeaturesList;
	@Getter("getCustomMapViewTypes") public customMapViewTypes;
	@Getter public getMapType: any;
	@Getter public getHideLabels: any;
	@Mutation public setMapType: any;	

	/* External map layers functions */
	@ExternalMapLayerStore.Getter("getExternalMapLayers") externalLayersList!: ExternalMapLayer[];
	@ExternalMapLayerStore.Action loadExternalMapLayers: () => Promise<void>;
	@ExternalMapLayerStore.Action updateExternalMapLayerVisiblity: (
		externalMapLayer: ExternalMapLayer
	) => Promise<void>;

	public elevation: number = 0;
	public displayElevation: string = "";
	public elevationList: any[] = [];

	public layersAdded = new LayerMap();
	public layersVisible = false;

	public itemsAdded: { [key: number]: IMarker } = {};
	public tempWaypoints: any[] = [];
	public markersByTypeAndObject = new ItemTypeObjectMap();
	private markerTypeVisibilityByID = new LayerTypeMap();
	private mapLayerItemTypes: MapItemTypeData[] = [];
	public drawnTourLines: any[] = [];	

	public enhancedMapping: IEnhancedMapping | null = null;
	public map: IMap | null = null;
	public mapCenter: ILatLng | null = null;
	public mapType: string = "";
	
	private isLoading: boolean = false;	

	// EMIT FUNCTIONS

	@Emit("visible-map-items")
	public emitVisibleMapItems() {
		const visibleMapDataItems = this.mapItemList.filter(mapItemData => {
			// Find the mapDataItems that have been marked as visible in itemsAdded
			const mapItem = this.itemsAdded[mapItemData.mapLayerItemID];
			if (mapItem && mapItem.visible) {
				return mapItem;
			}			
		});
		return visibleMapDataItems;
	}

	@Emit("add-item-to-tour")
	public addItemToGuardTour(item: MapItemData) {}

	// WATCH FUNCTIONS

	@Watch("elevationList", { deep: true })
	private updateDisplayElevation() {
		if (this.displayElevation == "" && this.elevationList.length > 0) {
			this.displayElevation = this.elevationList[0].name;
		}
	}

	@Watch("mapLayers")
	public mapLayersChanged(mapLayers) {
		if(this.currentGuardTour) {
			this.drawTourLines(this.currentGuardTour.guardTourSteps);
		}
	}

	@Watch("currentGuardTour", {deep: true})
	public async onSelectedGuardTourChange(guardTour: GuardTour) {
		if (guardTour) {
			this.drawTourLines(guardTour.guardTourSteps);
		}
		else {
			this.clearTourLines();
		}
	}

	@Watch("focusOnTour")
	public async onFocusOnTourChanged(focus: boolean) {
		this.clearMap();
		await this.updateMap();
	}

	@Watch("tourUpdatedOrCancelled")
	public async onTourUpdatedOrCancelledChanged() {
		this.clearMap();
		await this.updateMap();
	}

	@Watch("externalLayersList")
	async onExternalMapLayersListChanged(list: ExternalMapLayer[]) {
		// send list of layers to mapping library, all logic logic and functions is handled in there
		if (this.enhancedMapping) {
			await this.enhancedMapping.loadExternalMapLayers(list, this.map.map);
		}
	}

	// COMPONENT LIFECYCLE FUNCTIONS

	public async mounted() {
		await this.loadMapLayersItemTypes();
		await this.init();		
	}

	public async beforeDestroy() {
		if (this.enhancedMapping) {
			await this.enhancedMapping.unloadExternalMapLayers(this.externalLayersList);
		}

		if (this.map) {
			this.map.reset();
		}

		this.clearTourLines()
		this.clearMap();
	}

	public destroyed() {
		this.clearMap();
	}

	// COMPUTED PROPERTIES

	public get externalMapLayersEnabled(): boolean {
		return get(this.featuresList, ["ExternalMapLayers"]);
	}

	public get canGoUp() {
		return this.elevationList.some(elevation => elevation.elevationValue > this.elevation);
	}

	public get canGoDown() {
		const index = this.elevationList.findIndex(x => x.elevationValue == this.elevation);
		if (index == 0 || index == -1) {
			return false;
		}

		return this.elevationList.some(elevation => elevation.elevationValue < this.elevation);
	}

	private get guardTourStepMapLayerItemIds() {
		let mapLayerIds : number[] = [];
		for (var key in this.currentGuardTour.guardTourSteps) {
			if (this.currentGuardTour.guardTourSteps.hasOwnProperty(key)) {
				const guardTourStep = this.currentGuardTour.guardTourSteps[key];
				mapLayerIds.push(guardTourStep.mapLayerItemId);
			}
		}

		return mapLayerIds;
	}

	// MAP FUNCTIONS
	private async init() {
		this.enhancedMapping = await getEnhancedMapping("google", this.mapKey);
		let pos: LocationBounds = {
			location: { lat: 0, lng: 0 },
			viewport: null
		};

		this.mapCenter = pos.location;
		this.map = this.enhancedMapping.createMap(
			this.$refs.mappingContainer,
			pos.location,
			2,
			false,
			this.getMapType,
			getMapStyle(this.getMapType, this.getHideLabels),
			this.customMapViewTypes
		);

		this.mapType = this.getMapType;

		this.map!.registerEvent("onMapTypeChanged", (mapType: string) => {
			this.mapType = mapType;
			this.setMapType(mapType);

			this.map!.map.setOptions({
				styles: getMapStyle(this.getMapType, this.getHideLabels)
			});
		});

		this.map!.registerEvent("onViewportChangedDelayed", () => {
			if (this.map!.currentViewState == "narrow") {
				this.updateMap();
			} else {
				this.clearMap();
			}
		});

		this.map!.registerEvent("onDisplayTypeChanged", (data: any) => {
			var layersVisible = data.viewState === "narrow";

			for (var layerId in this.layersAdded) {
				this.layersAdded[layerId].visible = layersVisible;
			}

			for (var layerItemId in this.itemsAdded) {
				this.itemsAdded[layerItemId].visible = layersVisible;
			}
		});

		this.map!.registerEvent("onClick", (latLong: any) => {
			if (this.creatingWaypoint) {
				this.setWaypointMarkerPosition(latLong.lat + " " + latLong.lng);
			}
		});

		if (this.externalMapLayersEnabled === true) {
			this.loadExternalMapLayers();
		}

		this.addWaypointMapItemType();

		// Initialize the dictionary that stores the item type visibility
		this.mapLayerItemTypes.forEach(markerType => {
			this.markerTypeVisibilityByID[markerType.mapLayerItemTypeID] = markerType.visible;
		});
	}

	private addWaypointMapItemType() {
		this.mapLayerItemTypes = this.mapItemTypes.slice();

		this.mapLayerItemTypes.push({
			mapLayerItemTypeID: 101,
			title: "Waypoint",
			visible: true,
			hideByDefault: false,
			icon: this.mapIcons[101]
		});
	}

	private async loadInBounds(mapBounds: any, layersVisible: boolean) {
		this.isLoading = true;

		this.layersVisible = layersVisible;

		let bounds = {
			north: mapBounds.north,
			south: mapBounds.south,
			east: mapBounds.east,
			west: mapBounds.west
		} as MapBounds;

		if (this.elevation) bounds.elevation = this.elevation;

		let response = await this.loadMapLayers(bounds);
		let mapData = response.data;
		const elevations = mapData.elevations.slice(); // make a deep copy,
		// preventing mutation state outside mutation handlers.
		// Proper way would be to spread this logic accross the
		// `action -> service -> mutation -> getter` chain

		this.elevationList = elevations.sort(function(a, b) {
			return a.elevationValue - b.elevationValue;
		});

		if (this.elevation) {
			let currentElevation = elevations.find(
				(elevationFloor: any) => elevationFloor.elevationValue == this.elevation
			);
			if (currentElevation != null || elevations && elevations.length > 0) {
				this.elevation = currentElevation.elevationValue;
				this.displayElevation = currentElevation.name;
			}
		} else if (elevations && elevations.length > 0) {
			let currentElevation = this.elevationList[0];
			if (currentElevation != null) {
				this.elevation = currentElevation.elevationValue;
				this.displayElevation = currentElevation.name;
			}
		}

		if (mapData.layers) {
			this.displayLayers(mapData.layers as MapLayerData[]);
		}

		// Initialize our unique map item dictionary
		let uniqueItems: { [key: string]: MapItemData } = {};

		// Sort the items using by MapLayerItemID value 
		let mapDataItems = mapData.items.slice(); // appease the Vuex mutation Gods (see elevations above)
		mapDataItems.sort((a, b) => {
			return a.MapLayerItemID - b.MapLayerItemID;
		});

		// Find unique map items based on the type, latlong, minElevation, maxElevation and objectID
		mapDataItems = mapData.items.filter(x => {
			const key = x.mapLayerItemTypeID + "|" + x.latLong + "|" + x.minElevation + "|" + x.maxElevation + "|" + x.objectID;
			if (!uniqueItems[key]) {
				uniqueItems[key] = x;
				return true;
			}
		});

		// Log the markers that will be plotted
		const cameraItems = mapDataItems.filter(x => x.mapLayerItemTypeID === 1);
		const relayItems = mapDataItems.filter(x => x.mapLayerItemTypeID === 3);
		const alarmItems = mapDataItems.filter(x => x.mapLayerItemTypeID === 4);
		console.log(`Items loaded: ${mapData.items.length}, Unique items: ${mapDataItems.length} (Cameras: ${cameraItems.length}, Relays: ${relayItems.length}, Alarms: ${alarmItems.length})`);

		// Add any new waypoints to the list of items to be displayed
		if (this.tempWaypoints.length != 0) {
			this.tempWaypoints.forEach(waypoint => {
				mapDataItems.push(waypoint.mapData);
			})
		}

		if (mapDataItems) {
			//These are coming from the DB so need to set as saved=true
			mapDataItems.map((mapDataItem) => { 
				mapDataItem.saved = true; 
				return mapDataItem;
			});

			this.displayItems(mapDataItems);
		}
	}

	private clearMap() {
		for (var layerId in this.layersAdded) {
			let layer = this.layersAdded[layerId];

			layer.remove();
			delete this.layersAdded[layerId];
		}

		for (var itemId in this.itemsAdded) {
			let item: IMarker = this.itemsAdded[itemId];

			if (item.marker != null) {
				item.state = "";
				item.map = null;

				if (this.markersByTypeAndObject[item.mapLayerItemTypeID] != null) {
					if (this.markersByTypeAndObject[item.mapLayerItemTypeID][item.objectID!] != null) {
						delete this.markersByTypeAndObject[item.mapLayerItemTypeID][item.objectID!];
					}
				}

				item.remove();
				delete this.itemsAdded[itemId];
			}
		}

		this.clearUnsavedWaypoints();
	}

	private async updateMap() {
		if (this.map != null) {
			await this.loadInBounds(this.map!.mapBounds, this.map!.currentViewState == "narrow");
		}
	}

	// LAYERS
	private displayLayers(layers: MapLayerData[]) {
		const itemLayerIDs: string[] = [];

		layers.forEach(layer => {

			const elevation = this.elevation;

			let isElevated =
				(layer.minElevation == null || layer.minElevation! <= elevation) &&
				(layer.maxElevation == null || layer.maxElevation! >= elevation);

			if (isElevated) {
				itemLayerIDs.push(layer.mapLayerID.toString());

				if (this.layersAdded[layer.mapLayerID] == undefined) {
					this.layersAdded[layer.mapLayerID] = this.enhancedMapping.createOverlay(
						this.map!,
						layer.mapLayerID,
						layer.title,
						stringToBounds(layer.ne, layer.sw), // boundaries
						layer.rotation, // rotation
						axiosInstance.defaults.baseURL.endsWith("/") ? axiosInstance.defaults.baseURL + "MapLayers/Image/" + layer.mapLayerID.toString() : axiosInstance.defaults.baseURL + "/MapLayers/Image/" + layer.mapLayerID.toString() , // imageUrl
						layer.minElevation, // minElevation,
						layer.maxElevation, // maxElevation
						this.layersVisible // , visible
					);
				}
			}
		});

		for (var layerId in this.layersAdded) {
			if (itemLayerIDs.indexOf(layerId) == -1) {
				let layer = this.layersAdded[layerId];

				layer.remove();
				delete this.layersAdded[layerId];
			}
		}
	}

	private async toggleExternalMapLayerVisibility(mapLayerItem: ExternalMapLayer) {
		await this.updateExternalMapLayerVisiblity(mapLayerItem);
	}

	// ITEMS
	private displayItems(mapItems: MapItemData[]) {
		mapItems.forEach(mapItem => {
			if(mapItem) {
				if (
					(mapItem.minElevation == null || mapItem.minElevation! <= this.elevation) &&
					(mapItem.maxElevation == null || mapItem.maxElevation! >= this.elevation)
				) {
					// Make the items shown as active
					var state = "active";

					//Redraw the icon
					this.displayItemOnMap(mapItem, state);
				}
			}			
		});

		this.setMapItemList(mapItems);
		this.emitVisibleMapItems();

		this.isLoading = false;
	}	

	public displayItemOnMap(mapItemData: MapItemData, state: string) {
		//New waypoints only
		if (mapItemData.mapLayerItemTypeID == 101 && !mapItemData.saved) {
			const existingWaypointMarker = this.tempWaypoints.find(item => 
				item && item.mapItem.latLong == mapItemData.latLong && item.mapItem.title == mapItemData.title
			);

			if (existingWaypointMarker)
			{
				return;
			}

			mapItemData.mapLayerItemID = this.tempWaypoints.length;

			if (!this.tempWaypoints[mapItemData.mapLayerItemID]) {
				let mapMarkerItem = this.enhancedMapping.createMarker(
					this.map!,
					mapItemData.mapLayerItemID,
					null,
					mapItemData.mapLayerItemTypeID,
					mapItemData.objectID,
					mapItemData.title,
					convertStringToLatLng(mapItemData.latLong!),
					mapItemData.regionPath,
					mapItemData.minElevation,
					mapItemData.maxElevation,
					this.layersVisible
				);

				this.tempWaypoints[mapItemData.mapLayerItemID] = { marker: mapMarkerItem, mapItem: mapItemData };
				mapMarkerItem.state = state;
				mapMarkerItem.visible = true;
				mapMarkerItem.markerTypeVisible = true;
			}
		}
		else
		{
			const existingMapMarker = this.findMapMarker(mapItemData.mapLayerItemID);
			if (!existingMapMarker) {
				this.createMapMarker(mapItemData, true, state);
			}			
		}
	}

	private displayGuardTourItems() {
		if (!this.currentGuardTour || !this.currentGuardTour.guardTourSteps) {
			return;
		}	

		this.currentGuardTour.guardTourSteps.forEach(tourStep => {
			var mapItem: MapItemData = {
				mapLayerItemID: tourStep.mapLayerItemId,
				mapLayerItemTypeID: tourStep.mapLayerItemTypeId,
				minElevation: tourStep.minElevation,
				maxElevation: tourStep.minElevation,
				title: tourStep.mapLayerItemTitle,
				latLong: tourStep.latLong,
				objectID: null,
				extraValue: "",
				mapLayerIds: null,
				regionPath: null,
				saved : true,
				locationIdentifiers: [],
				brokenObjectLink: false
			};

			if (
				(mapItem.minElevation == null || mapItem.minElevation! <= this.elevation) &&
				(mapItem.maxElevation == null || mapItem.maxElevation! >= this.elevation)
			) {
				//The item is already displayed on the map so it doesn't need to be displayed again
			} else {
				// If the item is used in the tour, but on another layer show it as greyed out
				var state = "";
				this.displayItemOnMap(mapItem, state);
			}
		});
	}

	// MAP MARKERS
	private toggleVisibility(mapItemType: any) {
		var newMapItem = {
			...mapItemType,
			visible: !mapItemType.visible
		};

		this.setMapLayersItemType({ id: mapItemType.mapLayerItemTypeID, data: newMapItem });

		if (this.markersByTypeAndObject[newMapItem.mapLayerItemTypeID] != null) {
			var ByObject = this.markersByTypeAndObject[newMapItem.mapLayerItemTypeID];

			for (var objectId in ByObject) {
				ByObject[objectId].forEach(marker => (marker.markerTypeVisible = newMapItem.visible));
			}
		}

		// Update the visibility of marker types
		this.markerTypeVisibilityByID[newMapItem.mapLayerItemTypeID] = newMapItem.visible;
		let mapLayerItemType = this.mapLayerItemTypes.find(mapLayerItemType => mapLayerItemType.mapLayerItemTypeID == newMapItem.mapLayerItemTypeID);
		if (mapLayerItemType) {
			mapLayerItemType.visible = newMapItem.visible;
		}

		// Convert the dictionary of markers into an array of markers
		const mapItems = Object.values(this.itemsAdded);

		// Filter the markers based on the type that has been toggled
		const filteredMapItems = mapItems.filter(y => parseInt(y.mapLayerItemTypeID) === mapItemType.mapLayerItemTypeID);

		for (var mapItemID in filteredMapItems) {
			if (filteredMapItems.hasOwnProperty(mapItemID)) {				
				let mapItem = filteredMapItems[mapItemID];
				this.changeMapMarkerVisibility(mapItem.mapLayerItemID, newMapItem.visible);
			}
		}

		this.emitVisibleMapItems();
	}

	private changeMapMarkerVisibility(mapItemId: number, shouldBeVisible: boolean) {
		const mapItem: IMarker = this.findMapMarker(mapItemId);
		if (mapItem) {
			if (shouldBeVisible) {
				const mapItemData = this.mapItemList.find(x => x.mapLayerItemID === mapItem.mapLayerItemID);
				if (mapItemData) {
					this.createMapMarker(mapItemData, true);
				}
			} else {
				mapItem.visible = false;
				mapItem.map = null;
			}
		}
	}

	private findMapMarker(mapItemId: number) {
		const mapItem: IMarker = this.itemsAdded[mapItemId];
		if (mapItem) {
			return mapItem;
		} 

		return null;
	}

	private createMapMarker(mapItemData: MapItemData, visible: boolean = true, state: string = "active") {
		let canShow = visible;

		if (!this.markerTypeVisibilityByID[mapItemData.mapLayerItemTypeID]) {
			canShow = false;
		}

		if (this.currentGuardTour && this.focusOnTour) {
			if (!this.guardTourStepMapLayerItemIds.includes(mapItemData.mapLayerItemID)) {
				canShow = false;
			}
		}

		const mapMarkerItem = this.enhancedMapping.createMarker(
			this.map!,
			mapItemData.mapLayerItemID,
			null,
			mapItemData.mapLayerItemTypeID,
			mapItemData.objectID,
			mapItemData.title,
			convertStringToLatLng(mapItemData.latLong!),
			mapItemData.regionPath,
			mapItemData.minElevation,
			mapItemData.maxElevation,
			canShow
		);

		if (!canShow) {
			// Set the map property to null if we're not showing the marker
			mapMarkerItem.map = null;
		}

		mapMarkerItem.state = state;

		mapMarkerItem.click = () => {
			this.addItemToGuardTour(mapItemData);
			if (this.currentGuardTour) {
				this.drawTourLines(this.currentGuardTour.guardTourSteps);
			}
		};

		// Add it to the dictionary of markers
		this.itemsAdded[mapItemData.mapLayerItemID] = mapMarkerItem;

		return mapMarkerItem;
	}

	private clearUnsavedWaypoints() {
		for (let itemId = 0; itemId < this.tempWaypoints.length; itemId++) {

			if (this.tempWaypoints[itemId]) {

				let item: IMarker = this.tempWaypoints[itemId].marker;

				if (item.marker != null) {
					item.state = "";
					item.remove();
					delete this.tempWaypoints[itemId];
				}
			}

		}
	}
	
	// TOUR FUNCTIONS
	private drawTourLines(guardTourSteps: GuardTourStep[]) {
		this.clearTourLines();
		this.displayGuardTourItems();

		var lineColor = "slategray";
		var arrowColor = "slategray";
		var opacity = 0.9;

		var previousTourStep = null;

		if (!guardTourSteps) {
			return;
		}

		guardTourSteps.forEach(tourStep => {
			var layerItemLocation = new google.maps.LatLng(convertStringToLatLng(tourStep.latLong));
			var previousStepLocation = previousTourStep ? new google.maps.LatLng(convertStringToLatLng(previousTourStep.latLong)) : null;

			if (layerItemLocation) {

				if (previousStepLocation != null) {

					// Calculate latlngs positions, leaving a 1m gap between the map item and the line
					var gapBetweenArrows = 1;
					var distance = google.maps.geometry.spherical.computeDistanceBetween(previousStepLocation, layerItemLocation);

					var lineStartPosition = google.maps.geometry.spherical.interpolate(previousStepLocation, layerItemLocation, gapBetweenArrows/distance);
					var lineEndPosition = google.maps.geometry.spherical.interpolate(layerItemLocation, previousStepLocation, gapBetweenArrows/distance);

					if ((previousTourStep.minElevation! < this.elevation || previousTourStep.minElevation! > this.elevation) && tourStep.minElevation == this.elevation) {
						// If the tour step moves onto the selected layer
						lineColor = "slategray";
						arrowColor = "#2b78e4";
					}
					else if (previousTourStep.minElevation! == this.elevation && (tourStep.minElevation > this.elevation || tourStep.minElevation < this.elevation)) {
						// If the tour step moves from the selected layer
						lineColor = "#2b78e4";
						arrowColor = "slategray";
					}
					else if (
						(previousTourStep.minElevation == null || previousTourStep.minElevation! <= this.elevation) &&
						(previousTourStep.maxElevation == null || previousTourStep.maxElevation! >= this.elevation)
					)
					{
						// If the tour step takes place in the selected layer
						lineColor = "#2b78e4";
						arrowColor = "#2b78e4"
						opacity = 0.9;
					}
					else {
						// If the tour step does not take place in the selected layer
						lineColor = "slategray";
						arrowColor = "slategray";
					}

					var arrowSymbol = {
						path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
						strokeColor: arrowColor,
						fillColor: arrowColor,
						fillOpacity: opacity,
						scale:4
					};

					var line = new google.maps.Polyline({
						path: [
							lineStartPosition,
							lineEndPosition
						],
						strokeColor: lineColor,
						strokeWeight: 4,
						strokeOpacity: opacity,
						icons: [{
							icon: arrowSymbol,
							offset: '100%'
							}],
							zIndex: 5
					});

					this.drawnTourLines.push(line);

					line.setMap(this.map!.map)
				}

				previousTourStep = tourStep;
			}
		});
	}

	private clearTourLines() {
		this.drawnTourLines.forEach(item => {
			item.setMap(null);
		});
	}	

	// ELEVATION FUNCTIONS
	private upLevel() {
		let higherFloors = this.elevationList.filter(elevation => elevation.elevationValue > this.elevation);
		if (higherFloors.length > 0) {
			this.elevation = higherFloors[0].elevationValue;
			this.clearMap();
			this.updateMap();
		}
	}

	private downLevel() {
		let lowerFloors = this.elevationList.filter(elevation => elevation.elevationValue < this.elevation);
		if (lowerFloors.length > 0) {
			this.elevation = lowerFloors[lowerFloors.length - 1].elevationValue;
			this.clearMap();
			this.updateMap();
		}
	}

	public gotoElevation(elevation: number) {
		if(this.elevation != elevation) {
			this.displayElevation = this.elevationList.filter(elevation => elevation.elevationValue = elevation)[0].elevationName;
			this.elevation = elevation;
			this.clearMap();
			this.updateMap();
		}
	}
}
